import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import QUERY_KEYS from '../query/queryKeys';
import { fetchUserProjectAccess } from '../modules/users';

import { useUser } from '../context/UserContext';
import { useProject } from '../context/ProjectContext';

export const useRole = () => useUser().role;
export const useProjectRole = () => useProject().projectRole;

// Org-level permissions
export const usePermissions = () => {
  const { role, userPermissions, permissionSet } = useUser();

  return useMemo(() => ({
    role,
    permissions: userPermissions,
    hasPermission: (p) => permissionSet.has('*') || permissionSet.has(p),
    hasAnyPermission: (ps) => ps.some((p) => permissionSet.has('*') || permissionSet.has(p)),
    hasAllPermissions: (ps) => ps.every((p) => permissionSet.has('*') || permissionSet.has(p)),
    hasRoleIn: (roles = []) => !!role && roles.includes(role),
  }), [role, userPermissions, permissionSet]);
};

// Project-level permissions (with org fallback already baked into context)
export const useProjectPermissions = () => {
  const { projectRole, projectPermissions, permissionSet } = useProject();

  return useMemo(() => ({
    role: projectRole,
    permissions: projectPermissions,
    hasPermission: (p) => permissionSet.has('*') || permissionSet.has(p),
    hasAnyPermission: (ps) => ps.some((p) => permissionSet.has('*') || permissionSet.has(p)),
    hasAllPermissions: (ps) => ps.every((p) => permissionSet.has('*') || permissionSet.has(p)),
    hasRoleIn: (roles = []) => !!projectRole && roles.includes(projectRole),
  }), [projectRole, projectPermissions, permissionSet]);
};

export const useUserAccess = () => {
  const { data: userAccess, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEYS.users.projectAccess(),
    queryFn: fetchUserProjectAccess,
  });

  const {
    is_full_access: isFullAccess = false,
    is_executive_manager: isExecutiveManager = false,
    is_project_admin: isProjectAdmin = false,
    project_ids: projectIds = [],
  } = userAccess || {};

  return {
    isFullAccess,
    isExecutiveManager,
    isProjectAdmin,
    projectIds,
    canSeeAllDefects: isFullAccess || isExecutiveManager || isProjectAdmin,
    error,
    isError,
    isLoading,
  };
};